import React from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import type { TaxBreakdownEntry } from "../../../common/jsonRpcTypes";

type PricingSummaryVariant = "footer" | "mobile" | "summary";

function getDisplayTotal(
  isTaxExclusive: boolean,
  totalGross?: number,
  totalWithService?: number,
  subtotal?: number,
): number {
  if (isTaxExclusive) return totalGross ?? totalWithService ?? subtotal ?? 0;
  if (totalWithService && totalWithService !== subtotal) return totalWithService;
  return subtotal ?? 0;
}

function renderTaxRows(
  entries: TaxBreakdownEntry[],
  currency: string,
  countryCode: string,
  rowClassName: string,
): React.ReactNode {
  return entries.map((entry) => (
    <div key={entry.label} className={rowClassName}>
      <p>{entry.label}</p>
      <p>{formatCurrency(entry.amount, currency, countryCode)}</p>
    </div>
  ));
}

interface PricingSummaryProps {
  /** Layout variant: "footer" for desktop basket footer, "mobile" for basket accordion, "summary" for order summary */
  variant: PricingSummaryVariant;
  /** Subtotal before service charge */
  subtotal?: number;
  /** Total including service charge */
  totalWithService?: number;
  /** Service charge monetary amount */
  serviceChargeAmount?: number;
  /** Service charge percentage (e.g. 12.5) */
  serviceChargePercentage?: number;
  /** Currency code (e.g. "GBP") */
  currency: string;
  /** Country code (e.g. "GB") */
  countryCode: string;
  /** When `false`, renders tax-exclusive layout (Net → breakdown → Total). */
  taxInclusive?: boolean;
  /** Tax breakdown line items from the API. */
  taxBreakdown?: TaxBreakdownEntry[];
  /** Net subtotal (before tax) for tax-exclusive display. */
  totalNet?: number;
  /** Gross total (including tax) for tax-exclusive display. */
  totalGross?: number;
  /** Tax label from API (e.g. "VAT", "Sales Tax"). Falls back to "VAT". */
  taxLabel?: string;
}

/**
 * Unified pricing summary displaying service charge and total.
 *
 * - **footer** variant: renders inside the desktop basket footer with
 *   `basket-footer` class styling.
 * - **mobile** variant: renders inside the mobile basket accordion with
 *   `mobile-pricing-summary` wrapper.
 * - **summary** variant: renders inside the order summary with
 *   `order-totals` wrapper and subtotal line.
 */
const PricingSummary: React.FC<PricingSummaryProps> = ({
  variant,
  subtotal = 0,
  totalWithService,
  serviceChargeAmount = 0,
  serviceChargePercentage = 0,
  currency,
  countryCode,
  taxInclusive,
  taxBreakdown,
  totalNet,
  totalGross,
  taxLabel,
}) => {
  const { t } = useTranslation();
  const label = taxLabel ?? "VAT";
  const isTaxExclusive = taxInclusive === false && Array.isArray(taxBreakdown) && taxBreakdown.length > 0;

  const serviceChargeRow =
    serviceChargeAmount > 0 ? (
      <div className="service-charge-row gcw:flex gcw:justify-between gcw:items-center">
        <p>
          {t("discretionary_service_charge", "Discretionary service charge")}
          {serviceChargePercentage > 0 && ` (${serviceChargePercentage}%)`}
        </p>
        <p>{formatCurrency(serviceChargeAmount, currency, countryCode)}</p>
      </div>
    ) : null;

  if (variant === "footer") {
    const footerTotal = getDisplayTotal(isTaxExclusive, totalGross, totalWithService, subtotal);
    return (
      <>
        {serviceChargeRow}
        {isTaxExclusive &&
          renderTaxRows(
            taxBreakdown,
            currency,
            countryCode,
            "gcw:flex gcw:justify-between gcw:items-center gcw:border-0 gcw:!ms-4 gcw:me-4 radius-o gcw:!shadow-none",
          )}
        <div className="basket-footer gcw:flex gcw:justify-between gcw:items-center">
          <p>
            {t("total_label", "Total:")}
            {!isTaxExclusive && (
              <span className="total-price">{t("inc_vat", "inc {{taxLabel}}", { taxLabel: label })}</span>
            )}
          </p>
          <p
            className={
              totalWithService && totalWithService !== subtotal && !isTaxExclusive ? "original-price" : undefined
            }
          >
            {formatCurrency(footerTotal, currency, countryCode)}
          </p>
        </div>
      </>
    );
  }

  if (variant === "mobile") {
    const mobileTotal = getDisplayTotal(isTaxExclusive, totalGross, totalWithService, subtotal);
    return (
      <div className="mobile-pricing-summary gcw:p-bs-3">
        {serviceChargeRow}
        {isTaxExclusive &&
          renderTaxRows(
            taxBreakdown,
            currency,
            countryCode,
            "tax-breakdown-row gcw:flex gcw:justify-between gcw:items-center",
          )}
        <div className="basket-total-row gcw:flex gcw:justify-between gcw:items-center">
          <p>
            {t("total_label", "Total:")}
            {!isTaxExclusive && (
              <span className="total-price-label">{t("inc_vat", "inc {{taxLabel}}", { taxLabel: label })}</span>
            )}
          </p>
          <p className="total-price-value">{formatCurrency(mobileTotal, currency, countryCode)}</p>
        </div>
      </div>
    );
  }

  // summary variant
  const total = subtotal + serviceChargeAmount;

  if (isTaxExclusive) {
    return (
      <div className="order-totals">
        <div className="total-line">
          <span>{t("net_label", "Net")}</span>
          <span>{formatCurrency(totalNet ?? subtotal, currency, countryCode)}</span>
        </div>
        {serviceChargeAmount > 0 && serviceChargeRow}
        {renderTaxRows(taxBreakdown, currency, countryCode, "total-line tax-breakdown-row")}
        <div className="total-line total-final">
          <span>{t("tax_total_label", "Total")}</span>
          <span>{formatCurrency(totalGross ?? total, currency, countryCode)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="order-totals">
      <div className="total-line">
        <span>{t("subtotal", "Subtotal")}</span>
        <span>{formatCurrency(subtotal, currency, countryCode)}</span>
      </div>
      {serviceChargeAmount > 0 && serviceChargeRow}
      <div className="total-line total-final">
        <span>
          {t("total_label", "Total")} ({t("inc_vat", "inc {{taxLabel}}", { taxLabel: label })})
        </span>
        <span>{formatCurrency(total, currency, countryCode)}</span>
      </div>
    </div>
  );
};

export default PricingSummary;
